import { useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { publicServerEndpoint } from "../services/helper";

export default function useSearchProducts(storeId, collectionId, searchQuery) {
  const queryClient = useQueryClient();
  const abortControllerRef = useRef(null);

  const fetchProducts = async ({ pageParam = 0, signal }) => {
    let url = `${publicServerEndpoint}/v3/product/store/${storeId}/collectionv2?page=${pageParam}&limit=${20}&source=web`;
    if (collectionId && collectionId !== "all") {
      url += `&collectionId=${collectionId}`;
    }
    if (searchQuery) {
      url += `&search=${searchQuery}`;
    }
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error("Failed to fetch");
    const data = await res.json();
    return {
      products: data.data.docs,
      nextPage: pageParam + 1,
      previousPage: pageParam > 1 ? pageParam - 1 : null,
      hasNextPage: data.data.hasNextPage,
    };
  };

  const {
    data,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ["products", storeId, collectionId, searchQuery],
    queryFn: fetchProducts,
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.nextPage : undefined),
    enabled: !!storeId && searchQuery.length > 2,
  });

  const onSearch = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // eslint-disable-next-line no-undef
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      queryClient.removeQueries(["products", storeId, collectionId]);
      refetch();
    } catch (err) {
      if (err.name === "AbortError") {
        console.log("Request aborted");
      } else {
        throw err;
      }
    }
  };

  return {
    data,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isLoading,
    error,
    onSearch,
    isFetching,
  };
}
